import { useRef, useCallback, useEffect } from 'react';
const DEAD_ZONE = 15;
const FULL_ZONE = 100;
function clamp(v, min, max) {
    return Math.max(min, Math.min(max, v));
}
function easeOut(t) {
    return 1 - Math.pow(1 - t, 3);
}
export function useSwipe({ threshold = 80, velocityThreshold = 500, onSwipeLeft, onSwipeRight, onDragProgress, }) {
    const state = useRef({
        startX: 0,
        startY: 0,
        dx: 0,
        isDragging: false,
        isAnimating: false,
        startTime: 0,
        cardEl: null,
    });
    // Store callbacks in refs to avoid stale closures
    const onSwipeLeftRef = useRef(onSwipeLeft);
    onSwipeLeftRef.current = onSwipeLeft;
    const onSwipeRightRef = useRef(onSwipeRight);
    onSwipeRightRef.current = onSwipeRight;
    const onDragProgressRef = useRef(onDragProgress);
    onDragProgressRef.current = onDragProgress;
    const emitProgress = useCallback((dx) => {
        const cb = onDragProgressRef.current;
        if (!cb)
            return;
        const absDx = Math.abs(dx);
        if (absDx < DEAD_ZONE) {
            cb(0, 0);
        }
        else {
            const raw = clamp((absDx - DEAD_ZONE) / (FULL_ZONE - DEAD_ZONE), 0, 1);
            cb(easeOut(raw), dx < 0 ? -1 : 1);
        }
    }, []);
    // Shared: update card position during drag
    const handleMove = useCallback((clientX, clientY) => {
        const s = state.current;
        if (!s.isDragging || !s.cardEl)
            return;
        s.dx = clientX - s.startX;
        const dy = clientY - s.startY;
        // Don't hijack vertical scroll
        if (Math.abs(dy) > Math.abs(s.dx) && Math.abs(s.dx) < 15)
            return;
        const rotate = s.dx * 0.08;
        s.cardEl.style.transform = `translateX(${s.dx}px) rotate(${rotate}deg)`;
        emitProgress(s.dx);
    }, [emitProgress]);
    // Shared: finish drag — swipe or snap back
    const handleEnd = useCallback(() => {
        const s = state.current;
        if (!s.isDragging || !s.cardEl)
            return;
        s.isDragging = false;
        document.body.classList.remove('dragging');
        const velocity = Math.abs(s.dx) / (Date.now() - s.startTime) * 1000;
        // Left swipe (skip)
        if (s.dx < -threshold || (s.dx < -40 && velocity > velocityThreshold)) {
            s.isAnimating = true;
            onDragProgressRef.current?.(1, -1);
            const el = s.cardEl;
            el.classList.add('animating');
            el.style.transform = 'translateX(-120vw) rotate(-20deg)';
            setTimeout(() => {
                el.classList.remove('animating');
                el.style.transform = '';
                s.isAnimating = false;
                onDragProgressRef.current?.(0, 0);
                onSwipeLeftRef.current?.();
            }, 350);
        }
        // Right swipe (invite)
        else if (s.dx > threshold || (s.dx > 40 && velocity > velocityThreshold)) {
            s.isAnimating = true;
            onDragProgressRef.current?.(1, 1);
            s.cardEl.classList.add('animating');
            s.cardEl.style.transform = 'translateX(120vw) rotate(20deg)';
            setTimeout(() => {
                s.isAnimating = false;
                onDragProgressRef.current?.(0, 0);
                onSwipeRightRef.current?.();
            }, 350);
        }
        // Snap back — fade out hints smoothly
        else {
            s.isAnimating = true;
            s.cardEl.classList.add('snap-back');
            s.cardEl.style.transform = 'translateX(0) rotate(0deg)';
            const startDx = s.dx;
            const startP = (() => {
                const a = Math.abs(startDx);
                if (a < DEAD_ZONE)
                    return 0;
                return easeOut(clamp((a - DEAD_ZONE) / (FULL_ZONE - DEAD_ZONE), 0, 1));
            })();
            const startDir = startDx < 0 ? -1 : startDx > 0 ? 1 : 0;
            const fadeStart = performance.now();
            const fadeDuration = 300;
            const fadeHints = (now) => {
                const elapsed = now - fadeStart;
                const t = clamp(elapsed / fadeDuration, 0, 1);
                const p = startP * (1 - easeOut(t));
                onDragProgressRef.current?.(p, startDir);
                if (t < 1) {
                    requestAnimationFrame(fadeHints);
                }
                else {
                    s.isAnimating = false;
                }
            };
            requestAnimationFrame(fadeHints);
        }
    }, [threshold, velocityThreshold]);
    // Shared: begin drag
    const startDrag = useCallback((clientX, clientY, el) => {
        const s = state.current;
        // Block new drag while animating
        if (s.isAnimating)
            return false;
        s.startX = clientX;
        s.startY = clientY;
        s.dx = 0;
        s.isDragging = true;
        s.startTime = Date.now();
        s.cardEl = el;
        s.cardEl.classList.remove('animating', 'snap-back');
        document.body.classList.add('dragging');
        return true;
    }, []);
    // ── Touch events (mobile) ──
    const onTouchStart = useCallback((e) => {
        const t = e.touches[0];
        startDrag(t.clientX, t.clientY, e.currentTarget);
    }, [startDrag]);
    const onTouchMove = useCallback((e) => {
        const t = e.touches[0];
        handleMove(t.clientX, t.clientY);
    }, [handleMove]);
    const onTouchEnd = useCallback(() => {
        handleEnd();
    }, [handleEnd]);
    // ── Mouse events (desktop) ──
    const mouseMoveRef = useRef(() => { });
    const mouseUpRef = useRef(() => { });
    mouseMoveRef.current = (e) => {
        handleMove(e.clientX, e.clientY);
    };
    mouseUpRef.current = () => {
        handleEnd();
        document.removeEventListener('mousemove', onDocMouseMove);
        document.removeEventListener('mouseup', onDocMouseUp);
    };
    const onDocMouseMove = useCallback((e) => {
        mouseMoveRef.current(e);
    }, []);
    const onDocMouseUp = useCallback((e) => {
        mouseUpRef.current(e);
    }, []);
    const onMouseDown = useCallback((e) => {
        e.preventDefault();
        const started = startDrag(e.clientX, e.clientY, e.currentTarget);
        if (!started)
            return; // Animation in progress, ignore
        document.addEventListener('mousemove', onDocMouseMove);
        document.addEventListener('mouseup', onDocMouseUp);
    }, [startDrag, onDocMouseMove, onDocMouseUp]);
    // Cleanup on unmount
    useEffect(() => {
        return () => {
            document.removeEventListener('mousemove', onDocMouseMove);
            document.removeEventListener('mouseup', onDocMouseUp);
            document.body.classList.remove('dragging');
        };
    }, [onDocMouseMove, onDocMouseUp]);
    return { onTouchStart, onTouchMove, onTouchEnd, onMouseDown };
}
